import { useParams, useNavigate } from "react-router-dom";
import { BuyToken } from "./components/buy-token";
import { clearBenchCache, useBenchState } from "./use-bench-state";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { Disclaimer } from "./components/disclaimer";
import { calculateBenchState, N } from "@/lib/utils";
import { AssetInfo, BenchInfo } from "./components/bench-info";
import { useWallet } from "@txnlab/use-wallet";
import { clearBenchAppIdCache, useBenchAppId } from "./use-bench-appid";
import { clearBenchRegistryCache } from "./use-bench-registry";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Briefcase } from "lucide-react";
import config from "@/config";
import { useUserCountry } from "@/hooks/use-user-country";
import { Restricted } from "./components/restricted";
import { clearAlgoAcctInfoCache } from "@/hooks/use-algo-account";
import NotFound from "@/components/notfound";
import { clearAllBenchCache } from "./use-all-bench-states";
import { useListings } from "@/hooks/use-listings";
import { useMemo } from "react";
import { DSContractListing } from "@myth-finance/dualstake-ts-sdk";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { cx } from "class-variance-authority";
import { Link } from "@/components/links";

function DSBanner({
  listing,
  className,
}: {
  className?: string;
  listing: DSContractListing;
}) {
  const textColors = getHighlightClasses(listing.asaId, "text");
  return (
    <div className={cx("self-center mt-4 flex-col items-center", className)}>
      <div className="text-center mb-2 text-foreground">
        Want more {listing.asaUnitName}?
      </div>
      {/* <ListingCard listing={listing} /> */}
      <div className="text-sm px-1 mt-2 text-center text-muted-foreground">
        <Link to={`/dualSTAKE/${listing.lstName}`}><span className={textColors}>{listing.lstName}</span> dualSTAKE</Link>
      </div>
      <div className="px-1 text-sm text-center text-muted-foreground">
        Stake ALGO+{listing.asaUnitName}
      </div>
      <div className="px-1 text-sm text-center text-muted-foreground">
        Earn <span className={textColors}>{listing.asaUnitName}</span>
      </div>
    </div>
  );
}

export function BenchSingle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeAddress } = useWallet();
  const { data: dualstakes } = useListings();

  const {
    data: appId,
    isPending: appIdLoading,
    error: appIdError,
  } = useBenchAppId(id ? N(id) : undefined);
  const {
    isPending,
    error: benchError,
    refetch: refetchSingle,
    data,
  } = useBenchState(
    appId ?? undefined,
    config.benchRegistryAppId,
    activeAddress
  );
  const benchState = data ? calculateBenchState(data) : "";

  const {
    countryCode,
    loading: countryLoading,
    error: countryError,
  } = useUserCountry();

  const refetch = () => {
    refetchSingle();
    clearBenchRegistryCache();
    clearAlgoAcctInfoCache();
  };

  const loading =
    appIdLoading ||
    isPending ||
    (data?.meta?.bc?.length ? countryLoading : false);
  const error =
    appIdError ||
    benchError ||
    (data?.meta?.bc?.length ? countryError : undefined);

  const role =
    data?.ownerAddr === activeAddress
      ? "owner"
      : data?.feeAddr === activeAddress
      ? "fee manager"
      : undefined;

  const restricted = data?.meta?.bc?.find((b) => b === countryCode);

  const hasDualSTAKE = useMemo(() => {
    if (!data || !dualstakes || !dualstakes.size) return;
    for (const dualstake of dualstakes?.values() ?? []) {
      if (dualstake.asaId === data?.sellingAssetId) return dualstake;
    }
  }, [dualstakes, data]);

  if (!appIdLoading && !appIdError && appId === null) {
    return (
      <NotFound>
        <div>This bench does not exist.</div>
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => {
              clearBenchRegistryCache();
              clearAllBenchCache();
              clearBenchAppIdCache();
              clearBenchCache();
              window.location.reload();
            }}
          >
            RETRY
          </Button>
          <Button variant="outline" onClick={() => navigate("/bench")}>
            BACK
          </Button>
        </div>
      </NotFound>
    );
  }

  return (
    <WithErrorAndLoading loading={loading} error={error}>
      <div className="container grow xl:px-32 flex flex-col lg:flex-row lg:justify-between items-center lg:items-center mx-auto gap-4">
        <div className="flex flex-col items-center">
          <AssetInfo
            data={data!}
            className="hidden lg:flex"
            prequel={
              benchState !== "closed" && data?.meta?.tc ? (
                <Disclaimer href={data.meta.tc} className="hidden lg:flex" />
              ) : null
            }
          />
        </div>
        <div className="flex flex-col justify-center items-center gap-2">
          {benchState !== "closed" && data?.meta?.tc ? (
            <Disclaimer href={data.meta.tc} className="flex -mt-6 lg:hidden" />
          ) : null}
          {role && (
            <Card className="mx-auto w-full max-w-2xl mb-2">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-6 w-6 text-primary" />
                    <div>Bench role: {role}</div>
                  </div>
                  <Button
                    onClick={() => navigate(`/bench/${id}/manage`)}
                    variant="default"
                  >
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {restricted ? (
            <Restricted />
          ) : (
            <>
              <BuyToken data={data!} error={error?.message} refetch={refetch} />
              {hasDualSTAKE ? (
                <DSBanner className="block lg:hidden" listing={hasDualSTAKE} />
              ) : null}
            </>
          )}
        </div>
        <AssetInfo data={data!} className="flex lg:hidden" />
        <BenchInfo
          data={data!}
          tight={benchState === "closed" || !data?.meta?.tc}
        />
      </div>
      {hasDualSTAKE ? (
        <DSBanner className="hidden lg:block" listing={hasDualSTAKE} />
      ) : null}
    </WithErrorAndLoading>
  );
}
